import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';

import { EventCardComponent } from '../../../events/components/event-card/event-card.component';
import { EVENTS_COPY, UPCOMING_EVENTS_LIMITS } from '../../../events/constants/events.constants';
import { EventsService } from '../../../events/data/events.service';
import type { StoreEvent } from '../../../events/data/events.model';

/**
 * Anticipo de próximos eventos en la portada. Tres tarjetas y un enlace a la
 * agenda completa: la portada ya compite con una decena de bloques y esto no
 * pretende ser el calendario.
 *
 * **Los eventos no viajan dentro del payload de `/web/home`** (D2). Van en su
 * propia petición, con la degradación blindada: si `/upcoming` falla o viene
 * vacío, este bloque **no se pinta** —ni marco, ni título, ni hueco— y la
 * portada se sirve entera. Es la misma filosofía que ya rige el resto de la
 * home («si un bloque no viene, no se pinta»), y aquí pesa más porque esta web
 * renderiza en servidor: un fallo de datos sin contener se lleva la página.
 *
 * Una agenda vacía en portada, además, lee como tienda muerta.
 */
@Component({
  selector: 'app-eventos-block',
  imports: [EventCardComponent, RouterLink],
  templateUrl: './eventos-block.component.html',
  styleUrl: './eventos-block.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventosBlockComponent {
  private readonly eventsService = inject(EventsService);

  protected readonly copy = EVENTS_COPY;

  /**
   * Degradación por consumidor, que es como se hace en este repo (no hay
   * interceptor de errores): `catchError` → lista vacía + `console.error`.
   * Mismo patrón que el megamenú del header y la landing de juego.
   */
  private readonly upcoming = toSignal(
    this.eventsService.getUpcoming().pipe(
      catchError((err) => {
        console.error('[home/eventos] failed to load upcoming events', err);
        return of<StoreEvent[]>([]);
      }),
    ),
    { initialValue: [] as StoreEvent[] },
  );

  /** Corte de la portada sobre la respuesta canónica compartida (D10). */
  protected readonly events = computed(() =>
    this.upcoming().slice(0, UPCOMING_EVENTS_LIMITS.home),
  );

  protected readonly shouldRender = computed(() => this.events().length > 0);
}
